import { scopeProjectRef } from "@cmmd-forge/client-runtime";
import type { ThreadEnvMode } from "@cmmd-forge/contracts";
import { useRouter } from "@tanstack/react-router";
import { useState, type MouseEvent } from "react";

import { useComposerDraftStore } from "~/composerDraftStore";
import { usePrimaryEnvironmentId } from "~/environments/primary";
import type { useNewThreadHandler } from "~/hooks/useHandleNewThread";
import { useSettings } from "~/hooks/useSettings";
import { resolveNewThreadRuntimeProjectRef } from "~/lib/threadRuntimeTarget";
import { sanitizePublicUiError } from "~/productSafeLabels";
import { selectThreadByRef, useStore } from "~/store";
import { resolveThreadRouteTarget } from "~/threadRoutes";
import { toastManager } from "../ui/toast";
import {
  resolveSidebarNewThreadEnvMode,
  resolveSidebarNewThreadSeedContext,
} from "./newThreadWorkspace";
import { resolvePreConnectCloudNewThreadRoute } from "./preConnectCloudNewThreadRoute";
import {
  attachDesktopRuntimeThenCreateThread,
  requiresDesktopRuntimeAttach,
} from "./sidebarDesktopRuntimeThread";
import { resolveSidebarProjectRuntimeActionPolicy } from "./sidebarProjectRuntimeActions";
import type { SidebarProjectSnapshot } from "./SidebarProjectItem";
import { useCloseSidebarOnNavigate } from "./useCloseSidebarOnNavigate";

type NewThreadRuntimeProjectInput = Parameters<typeof resolveNewThreadRuntimeProjectRef>[0];
type ThreadRouteParams = Parameters<typeof resolveThreadRouteTarget>[0];
type NewThreadSeedContext = ReturnType<typeof resolveSidebarNewThreadSeedContext>;

function readActiveThreadSeed(routeParams: ThreadRouteParams, projectId: string) {
  const routeTarget = resolveThreadRouteTarget(routeParams);
  if (routeTarget?.kind !== "server") return null;
  const thread = selectThreadByRef(useStore.getState(), routeTarget.threadRef) ?? null;
  if (thread?.projectId !== projectId) return null;
  return {
    projectId: thread.projectId,
    branch: thread.branch,
    worktreePath: thread.worktreePath,
  };
}

function readActiveDraftThreadSeed(routeParams: ThreadRouteParams, projectId: string) {
  const routeTarget = resolveThreadRouteTarget(routeParams);
  const draftStore = useComposerDraftStore.getState();
  const draft =
    routeTarget?.kind === "server"
      ? (draftStore.getDraftThread(routeTarget.threadRef) ?? null)
      : routeTarget?.kind === "draft"
        ? (draftStore.getDraftSession(routeTarget.draftId) ?? null)
        : null;
  if (draft?.projectId !== projectId) return null;
  return {
    projectId: draft.projectId,
    branch: draft.branch,
    worktreePath: draft.worktreePath,
    envMode: draft.envMode,
  };
}

function resolveSidebarNewThreadRequest(input: {
  readonly routeParams: ThreadRouteParams;
  readonly targetProjectRef: NewThreadRuntimeProjectInput["projectRef"];
  readonly defaultThreadEnvMode: ThreadEnvMode;
  /** The project's own pin, which beats the app setting. Null when unpinned. */
  readonly projectThreadEnvMode: ThreadEnvMode | null;
  readonly defaultThreadRuntime: NewThreadRuntimeProjectInput["requestedRuntime"];
  readonly primaryEnvironmentId: NewThreadRuntimeProjectInput["primaryEnvironmentId"];
  readonly runtimeProjectCandidates: NewThreadRuntimeProjectInput["projects"];
}) {
  const projectId = input.targetProjectRef.projectId;
  const seedContext = resolveSidebarNewThreadSeedContext({
    projectId,
    defaultEnvMode: resolveSidebarNewThreadEnvMode({
      projectEnvMode: input.projectThreadEnvMode,
      defaultEnvMode: input.defaultThreadEnvMode,
    }),
    activeThread: readActiveThreadSeed(input.routeParams, projectId),
    activeDraftThread: readActiveDraftThreadSeed(input.routeParams, projectId),
  });
  const projectRef = resolveNewThreadRuntimeProjectRef({
    requestedRuntime: input.defaultThreadRuntime,
    primaryEnvironmentId: input.primaryEnvironmentId,
    projects: input.runtimeProjectCandidates,
    projectRef: input.targetProjectRef,
  });
  return { projectRef, seedContext };
}

function buildNewThreadOptions(seedContext: NewThreadSeedContext) {
  return {
    ...(seedContext.branch !== undefined ? { branch: seedContext.branch } : {}),
    ...(seedContext.worktreePath !== undefined ? { worktreePath: seedContext.worktreePath } : {}),
    envMode: seedContext.envMode,
  };
}

interface UseSidebarProjectNewThreadProps {
  readonly project: SidebarProjectSnapshot;
  readonly handleNewThread: ReturnType<typeof useNewThreadHandler>["handleNewThread"];
}

export function useSidebarProjectNewThread({
  project,
  handleNewThread,
}: UseSidebarProjectNewThreadProps) {
  const router = useRouter();
  const closeSidebarOnNavigate = useCloseSidebarOnNavigate();
  const defaultThreadEnvMode = useSettings<ThreadEnvMode>(
    (settings) => settings.defaultThreadEnvMode,
  );
  const defaultThreadRuntime = useSettings((settings) => settings.defaultThreadRuntime);
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const [isAttachingDesktopRuntime, setIsAttachingDesktopRuntime] = useState(false);

  const runtimeActionPolicy = resolveSidebarProjectRuntimeActionPolicy({
    projectName: project.name,
    source: project.source,
  });
  const runtimeProjectCandidates = [
    project,
    ...project.memberProjectRefs.map((memberRef) => ({
      environmentId: memberRef.environmentId,
      id: memberRef.projectId,
      repositoryIdentity: project.repositoryIdentity ?? null,
    })),
  ];
  const activeMember = project.activeWorktreeKey
    ? project.worktreeMembers.find((member) => member.worktreeKey === project.activeWorktreeKey)
    : undefined;
  const targetProjectRef =
    activeMember?.projectRef ?? scopeProjectRef(project.environmentId, project.id);
  const preConnectCloud = project.preConnectCloud ?? null;

  const handleCreateThreadClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (preConnectCloud) {
      void router.navigate(resolvePreConnectCloudNewThreadRoute(preConnectCloud));
      closeSidebarOnNavigate();
      return;
    }
    if (!runtimeActionPolicy.canCreateThread) {
      toastManager.add({
        type: "warning",
        title: runtimeActionPolicy.createThreadTitle,
        description: runtimeActionPolicy.createThreadDescription,
      });
      return;
    }
    if (isAttachingDesktopRuntime) return;
    const request = resolveSidebarNewThreadRequest({
      routeParams: router.state.matches[router.state.matches.length - 1]?.params ?? {},
      targetProjectRef,
      defaultThreadEnvMode,
      projectThreadEnvMode: project.defaultThreadEnvMode ?? null,
      defaultThreadRuntime,
      primaryEnvironmentId,
      runtimeProjectCandidates,
    });
    const options = buildNewThreadOptions(request.seedContext);
    if (!requiresDesktopRuntimeAttach(project)) {
      void handleNewThread(request.projectRef, options);
      closeSidebarOnNavigate();
      return;
    }
    void (async () => {
      setIsAttachingDesktopRuntime(true);
      try {
        await attachDesktopRuntimeThenCreateThread({
          environmentId: targetProjectRef.environmentId,
          primaryEnvironmentId,
          label: project.remoteEnvironmentLabels[0] ?? project.source.label,
          createThread: () => handleNewThread(request.projectRef, options),
          closeSidebar: closeSidebarOnNavigate,
        });
      } catch (error) {
        toastManager.add({
          type: "error",
          title: "Runtime connection failed",
          description: sanitizePublicUiError(
            error,
            "Forge could not connect to this desktop runtime.",
          ),
        });
      } finally {
        setIsAttachingDesktopRuntime(false);
      }
    })();
  };

  return {
    handleCreateThreadClick,
    isAttachingDesktopRuntime,
    runtimeActionPolicy,
  };
}
